import CryptoJS from 'crypto-js'

import api from '@/api'
import { APP_NAME, KEY_IS_LOGIN_WITH_AZURE, KEY_ME, KEY_MENU_ICONS,KEY_REFRESH_TOKEN, KEY_TOKEN } from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'

const encryptedTokenKey = btoa(KEY_TOKEN)
const encryptedRefreshTokenKey = btoa(KEY_REFRESH_TOKEN)
const encryptedMeKey = btoa(KEY_ME)
const encryptedIsLoginWithAzure = btoa(KEY_IS_LOGIN_WITH_AZURE)
const encryptedMenuIconsKey = btoa(KEY_MENU_ICONS)

const namespaced = true

const state = {
  isAuthenticated: !!localStorage.getItem(encryptedTokenKey),
  me: localStorage.getItem(encryptedMeKey) || null,
  token: localStorage.getItem(encryptedTokenKey) || null,
  refreshToken: localStorage.getItem(encryptedRefreshTokenKey) || null,
  isLoginWithAzure: localStorage.getItem(encryptedIsLoginWithAzure) || false,
  menuIcons: localStorage.getItem(encryptedMenuIconsKey) || {},
  isLoginAzureProcessing: false
}

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  me: state => state.me
    ? JSON.parse(CryptoJS.AES.decrypt(state.me, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  token: state => state.token
    ? CryptoJS.AES.decrypt(state.token, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  refreshToken: state => state.refreshToken
    ? CryptoJS.AES.decrypt(state.refreshToken, CRYPTO_KEY).toString(CryptoJS.enc.Utf8)
    : null,
  isLoginWithAzure: state => state.isLoginWithAzure,
  menuIcons: state => {
    if(state.menuIcons && typeof state.menuIcons === 'object') return state.menuIcons
    return state.menuIcons && typeof state.menuIcons === 'string' && !!JSON.parse(state.menuIcons) ? JSON.parse(state.menuIcons) : {}
  },
  isLoginAzureProcessing: state => state.isLoginAzureProcessing
}

const mutations = {
  SET_ME: (state, payload) => {
    state.me = payload
    localStorage.setItem(encryptedMeKey, payload)
  },
  SET_TOKEN: (state, payload) => {
    state.token = payload
    state.isAuthenticated = true
    localStorage.setItem(encryptedTokenKey, payload)
  },
  SET_REFRESH_TOKEN: (state, payload) => {
    state.refreshToken = payload
    localStorage.setItem(encryptedRefreshTokenKey, payload)
  },
  SET_IS_LOGIN_WITH_AZURE: (state, payload) => {
    state.isLoginWithAzure = payload
    localStorage.setItem(encryptedIsLoginWithAzure, payload)
  },
  SET_MENU_ICONS: (state, newIcons) => {
    const newIds = new Set(Object.keys(newIcons))

    // revoke removed icons
    Object.entries(state.menuIcons).forEach(([id, oldUrl]) => {
      if (!newIds.has(id)) {
        URL.revokeObjectURL(oldUrl)
      }
    })

    state.menuIcons = newIcons
    localStorage.setItem(encryptedMenuIconsKey, JSON.stringify(newIcons))
  },
  SET_IS_LOGIN_WITH_AZURE_PROCESSING: (state, payload) => {
    state.isLoginAzureProcessing = payload
  }
}

const CACHE_NAME = 'menu-icon-cache-v1'

export async function getCachedIcon(id, url) {
  if (!('caches' in window)) {
    // fallback langsung fetch tanpa cache
    const res = await fetch(url)
    const blob = await res.blob()
    return {
      src: URL.createObjectURL(blob),
      type: res.headers.get('Content-Type')
    }
  }

  const cache = await caches.open(CACHE_NAME)
  try {
    const res = await fetch(url)
    if(res) {
      await cache.delete(id)
      const cachedResponse = await cache.put(id, res.clone())
      const blob = await res.blob()
      return {
        src: URL.createObjectURL(blob),
        type: res.headers.get('Content-Type') // image/png | image/jpeg
      }
    }
  } catch(e) {
    // Continue
  }

  const cachedResponse = await cache.match(id)
  if (cachedResponse) {
    const blob = await cachedResponse.blob()
    const blobUrl = URL.createObjectURL(blob)
    const isValidBlob = await fetch(blobUrl);
    if (isValidBlob) {
      return {
        src: blobUrl,
        type: cachedResponse.headers.get('Content-Type') // image/png | image/jpeg
      }
    } else {
      return {
        src: null,
        type: null
      }
    }
  }

  throw new Error('Failed to fetch icon')
}

const actions = {
  async login({ commit, dispatch }, payload) {
    try {
      const { data } = await api.login(payload)
      commit('SET_TOKEN', CryptoJS.AES.encrypt(data.access_token, CRYPTO_KEY).toString())
      commit('SET_REFRESH_TOKEN', CryptoJS.AES.encrypt(data.refresh_token, CRYPTO_KEY).toString())

      await dispatch('fetchMe')
    } catch ({ data }) {
      throw data
    }
  },
  async loginDirect({ commit, dispatch }, payload) {
    commit('SET_TOKEN', CryptoJS.AES.encrypt(payload.access_token, CRYPTO_KEY).toString())
    commit('SET_REFRESH_TOKEN', CryptoJS.AES.encrypt(payload.refresh_token, CRYPTO_KEY).toString())
    commit('SET_IS_LOGIN_WITH_AZURE', false)

    await dispatch('fetchMe')
  },
  async fetchMe({ commit, dispatch }) {
    try {
      const { data } = await api.getMe()
      const permissions = data.permissions.filter(item => item.app_name === APP_NAME)
      const menuIcons = await dispatch('processMenuIcons', permissions)
      if(menuIcons && typeof menuIcons === 'object') commit('SET_MENU_ICONS', menuIcons)
      commit('SET_ME', CryptoJS.AES.encrypt(JSON.stringify({ ...data, permissions: data.permissions.filter(item => item.app_name === APP_NAME) }), CRYPTO_KEY).toString())
    } catch ({ data }) {
      throw data
    }
  },
  async refreshToken({ commit, getters }) {
    try {
      const tokenRefresh = getters['refreshToken']
      const { data } = await api.refreshToken({ refresh_token: tokenRefresh })
      commit('SET_TOKEN', CryptoJS.AES.encrypt(data.access_token, CRYPTO_KEY).toString())
      commit('SET_REFRESH_TOKEN', CryptoJS.AES.encrypt(data.refresh_token, CRYPTO_KEY).toString())
    } catch ({ data }) {
      throw data
    }
  },
  async logout({ commit }) {
    try{
      const response =   await api.logout()
      if(response.status === 200) {
        localStorage.removeItem(encryptedTokenKey)
        localStorage.removeItem(encryptedMeKey)
        localStorage.removeItem(encryptedIsLoginWithAzure)
      }
    } catch ({ data }) {
      throw data
    }
  },
  async loginWithAzure({ commit, dispatch, getters }, payload) {
    const isProcessing = getters['isLoginAzureProcessing']
    if(!isProcessing){
      try {
        const { access_token } = payload
        commit('SET_IS_LOGIN_WITH_AZURE_PROCESSING', true)
        const { data } = await api.exchangeToken(access_token)
        commit('SET_TOKEN', CryptoJS.AES.encrypt(data.access_token, CRYPTO_KEY).toString())
        commit('SET_REFRESH_TOKEN', CryptoJS.AES.encrypt(data.refresh_token, CRYPTO_KEY).toString())
        commit('SET_IS_LOGIN_WITH_AZURE', data.is_azure)
        await dispatch('fetchMe')
        commit('SET_IS_LOGIN_WITH_AZURE_PROCESSING', false)
      } catch (err) {
        commit('SET_IS_LOGIN_WITH_AZURE_PROCESSING', false)
        throw err
      }
    }
  },
  async sendOtpCode(_, payload) {
    payload = {
      ...payload,
      is_blms: true
    }
    try {
      const { data } = await api.sendOtpCode(payload)
      return data
    } catch ({ data }) {
      throw data
    }
  },
  async verifyOtpCode({ commit, dispatch }, payload) {
    try {
      const { data } = await api.verifyOtpCode(payload)
      commit('SET_TOKEN', CryptoJS.AES.encrypt(data.access_token, CRYPTO_KEY).toString())
      commit('SET_REFRESH_TOKEN', CryptoJS.AES.encrypt(data.refresh_token, CRYPTO_KEY).toString())

      await dispatch('fetchMe')
    } catch ({ data }) {
      throw data
    }
  },
  async resendOtpCode(_, payload) {
    payload = {
      ...payload,
      is_blms: true
    }
    try {
      const { data } = await api.resendOtpCode(payload)
      return data
    } catch ({ data }) {
      throw data
    }
  },
  async updatePermissions({ commit, dispatch }) {
    try {
      const me = getters.me(state)
      const { data } = await api.getPermissions()
      if(data.error) {
        return
      }
      me.permissions = data.filter(item => item.app_name === APP_NAME)
      const menuIcons = await dispatch('processMenuIcons', me.permissions)
      if(menuIcons && typeof menuIcons === 'object') commit('SET_MENU_ICONS', menuIcons)
      commit('SET_ME', CryptoJS.AES.encrypt(JSON.stringify(me), CRYPTO_KEY).toString())
    } catch (error) {
      throw error && error.data ? error.data : error
    }
  },
  async hydrateMenuIcons({ commit, getters, dispatch }) {
    try {
      const permissions = getters.me.permissions
      if(permissions) {
        const icons = await await dispatch('processMenuIcons', permissions)
        if(icons && typeof icons === 'object') commit('SET_MENU_ICONS', icons)
      }
    }catch(error){
      throw error
    }
  },
  async processMenuIcons(_,permissions) {
    try {
      const entries = await Promise.all(permissions.map(async p => {
        const newPermission = p;
        if(newPermission.icon && newPermission.icon.downloadUrl) {
          const imageUrl = await getCachedIcon(p.id, p.icon.downloadUrl)
          return [String(p.id), imageUrl]
        }
      }))
      // remove nulls
      return Object.fromEntries(entries.filter(Boolean))
    } catch (error) {
      throw error
    }
  }
}

export default { namespaced, state, getters, mutations, actions }
